import type { MouseEvent, KeyboardEvent } from 'react';
import { isEmpty, noop } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { Icon, IconSize } from 'app/components/core/Icon';
import { Button, ButtonSize, ButtonEdge } from 'app/components/core/Button';
import { Input, InputSize, InputVariant } from 'app/components/core/Input';
import * as styles from './FilterInput.css';

const preventFocusStealing = (event: MouseEvent) => event.preventDefault();

const closeButtonSize: Record<string, ButtonSize> = {
    [InputSize.MEDIUM]: ButtonSize.EXTRA_SMALL,
    [InputSize.LARGE]: ButtonSize.SMALL,
    [InputSize.EXTRA_LARGE]: ButtonSize.MEDIUM
};

const searchIconSize: Record<string, IconSize> = {
    [InputSize.MEDIUM]: IconSize.EXTRA_SMALL,
    [InputSize.LARGE]: IconSize.SMALL,
    [InputSize.EXTRA_LARGE]: IconSize.MEDIUM
};

interface FilterInputProps {
    value?: string | number;
    size?: InputSize;
    blurOnEnterKey?: boolean;
    onChange?: (value: string | number | null) => void;
    onClear?: () => void;
    onEnterKey?: (value: string | number | undefined, event: KeyboardEvent) => void;
    onEscapeKey?: (value: string | number | undefined) => void;
    [key: string]: unknown;
}

export function FilterInput({
    value,
    size = InputSize.LARGE,
    blurOnEnterKey = false,
    onChange = noop,
    onClear = noop,
    onEnterKey = noop,
    onEscapeKey = noop,
    ...props
}: FilterInputProps) {
    const { t } = useTranslation();

    function handleClear(event: MouseEvent) {
        event.stopPropagation();
        onChange('');
        onClear();
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case 'Enter':
                if (blurOnEnterKey) {
                    (event.target as HTMLInputElement).blur();
                }
                onEnterKey(value, event);
                break;
            case 'Escape':
                (event.target as HTMLInputElement).blur();
                onEscapeKey(value);
                break;
            default:
                break;
        }
    }

    return (
        <Input
            value={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            size={size}
            variant={InputVariant.OUTLINE}
            {...props}>
            {isEmpty(value) ? (
                <Icon className={styles.icon} variant="search" size={searchIconSize[size]} />
            ) : (
                <Button
                    onClick={handleClear}
                    onMouseDown={preventFocusStealing}
                    icon="close"
                    label={t('common:clear')}
                    size={closeButtonSize[size]}
                    edge={ButtonEdge.FLAT}
                />
            )}
        </Input>
    );
}
